import "./App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";

// Public marketing surface — eagerly loaded so first paint has no dynamic-import wait.
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import { INDUSTRIES } from "./data/industries";
import { LOCAL_AREAS } from "./data/localAreas";
import V2Home from "./pages/v2/Home";
import { WorkIndex as V2WorkIndex, WorkDetail as V2WorkDetail, StartAProject as V2StartAProject } from "./pages/v2/Pages";
import { AboutPage as V2About, ServicesIndex as V2ServicesIndex, ServiceDetail as V2ServiceDetail } from "./pages/v2/Services";
import { V2Terms, V2Privacy } from "./pages/v2/Legal";

// SEO cluster pages — lazy so hitting `/` doesn't download the code for 20
// other pages. Each loads on demand when its route is hit; prerendered HTML
// keeps SEO title/meta correct even before the chunk downloads.
const NorwichDigitalAgency = lazy(() => import("./pages/v2/seo/NorwichDigitalAgency"));
const NorwichWebDesign = lazy(() => import("./pages/v2/seo/NorwichWebDesign"));
const NorwichSeo = lazy(() => import("./pages/v2/seo/NorwichSeo"));
const NorwichGoogleAds = lazy(() => import("./pages/v2/seo/NorwichGoogleAds"));
const NorwichSocialMedia = lazy(() => import("./pages/v2/seo/NorwichSocialMedia"));
const NorwichSoftwareDevelopment = lazy(() => import("./pages/v2/seo/NorwichSoftwareDevelopment"));
const WebsiteRedesignVsRebuild = lazy(() => import("./pages/v2/seo/WebsiteRedesignVsRebuild"));
const CustomCrmDevelopment = lazy(() => import("./pages/v2/seo/CustomCrmDevelopment"));
const BusinessWebsiteCostUk = lazy(() => import("./pages/v2/seo/BusinessWebsiteCostUk"));
const SeoVsGoogleAds = lazy(() => import("./pages/v2/seo/SeoVsGoogleAds"));
const SaasB2bTech = lazy(() => import("./pages/v2/seo/SaasB2bTech"));
const LondonDigitalAgency = lazy(() => import("./pages/v2/seo/LondonDigitalAgency"));
const B2bDigitalMarketing = lazy(() => import("./pages/v2/seo/B2bDigitalMarketing"));
const RecruitmentAgencies = lazy(() => import("./pages/v2/seo/RecruitmentAgencies"));
const PrivateClinics = lazy(() => import("./pages/v2/seo/PrivateClinics"));
const WebsiteNotGeneratingLeads = lazy(() => import("./pages/v2/seo/WebsiteNotGeneratingLeads"));
const GoogleAdsClicksNoLeads = lazy(() => import("./pages/v2/seo/GoogleAdsClicksNoLeads"));
const WhenNeedCustomCrm = lazy(() => import("./pages/v2/seo/WhenNeedCustomCrm"));
const InHouseVsAgency = lazy(() => import("./pages/v2/seo/InHouseVsAgency"));
const AgencyVsGrowthPartner = lazy(() => import("./pages/v2/seo/AgencyVsGrowthPartner"));

// --- Lazy-loaded (not shipped to public visitors) ---------------------------
// Everything below is split into its own chunk. Public marketing pages don't
// download this code at all — cut ~350 KB off first-visit weight, dramatically
// improves LCP / TBT on brightlabs.live.

// Legacy pages, brand/india microsites, industry & area sub-pages
const AgencyHome = lazy(() => import("./pages/AgencyHome"));
const IndustryPage = lazy(() => import("./pages/IndustryPage"));
const Norwich = lazy(() => import("./pages/Norwich"));
const LocalArea = lazy(() => import("./pages/LocalArea"));
const PackagePage = lazy(() => import("./pages/PackagePage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const BuildPage = lazy(() => import("./pages/BuildPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const IndiaHome = lazy(() => import("./pages/IndiaHome"));

// CRM (all admin) — never loaded by public visitors
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminEmailSettings = lazy(() => import("./pages/admin/AdminEmailSettings"));
const AdminEnquiries = lazy(() => import("./pages/admin/AdminEnquiries"));
const AdminClients = lazy(() => import("./pages/admin/AdminClients"));
const AdminServicesHandout = lazy(() => import("./pages/admin/AdminServicesHandout"));
const DiscoveryPlaybook = lazy(() => import("./pages/admin/salestools/DiscoveryPlaybook"));
const PricingConfigurator = lazy(() => import("./pages/admin/salestools/PricingConfigurator"));
const AdminSentEmails = lazy(() => import("./pages/admin/AdminSentEmails"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminEmailTemplates = lazy(() => import("./pages/admin/AdminEmailTemplates"));
const AdminConversations = lazy(() => import("./pages/admin/AdminConversations"));
const AdminWebsiteAudit = lazy(() => import("./pages/admin/AdminWebsiteAudit"));
const AdminSeoPlaybook = lazy(() => import("./pages/admin/AdminSeoPlaybook"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));
const AdminActivity = lazy(() => import("./pages/admin/AdminActivity"));
const AdminOnboarding = lazy(() => import("./pages/admin/AdminOnboarding"));
const AdminPresentations = lazy(() => import("./pages/admin/presentations/AdminPresentations"));
const WhyWebsiteDeck = lazy(() => import("./pages/admin/presentations/WhyWebsiteDeck"));
const CustomCrmDeck = lazy(() => import("./pages/admin/presentations/CustomCrmDeck"));
const OnboardingPortal = lazy(() => import("./pages/onboarding/OnboardingPortal"));
const ClientReview = lazy(() => import("./pages/ClientReview"));
const PublicProposal = lazy(() => import("./pages/proposal/PublicProposal"));
const LeadEngine = lazy(() => import("./pages/admin/leadengine/LeadEngine"));

// Training decks — internal presenter tool
const TrainingHome = lazy(() => import("./pages/training/TrainingHome"));
const Day1 = lazy(() => import("./pages/training/Day1"));
const Day2 = lazy(() => import("./pages/training/Day2"));
const Day3 = lazy(() => import("./pages/training/Day3"));
const Day4 = lazy(() => import("./pages/training/Day4"));
const Roadmap = lazy(() => import("./pages/training/Roadmap"));
const NotesPopout = lazy(() => import("./pages/training/NotesPopout"));
const SalesCheatSheet = lazy(() => import("./pages/SalesCheatSheet"));

// V2 internal tooling — style tile / sitemap / dev index (not public marketing)
const V2Index = lazy(() => import("./pages/v2/V2Index"));
const V2StyleTile = lazy(() => import("./pages/v2/StyleTile"));
const V2Sitemap = lazy(() => import("./pages/v2/Sitemap"));
const V2InsightsLibrary = lazy(() => import("./pages/v2/V2InsightsLibrary"));
import AdminSeoPages from "./pages/admin/AdminSeoPages";
import AdminDeliveryPlaybooks from "./pages/admin/AdminDeliveryPlaybooks";

// Fires a GA4 page_view on every React Router navigation. No-ops gracefully
// if gtag isn't loaded (dev / preview without the analytics tag).
function GAPageTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: pathname + (search || ""),
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);
  return null;
}

// Scrolls window to top on every route change (preserves anchor links so #pricing etc still work).
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // let browser handle anchor jumps
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

// 301 map — legacy / alternate URLs that Google may have indexed or that
// people might type/link to. Client-side redirect via React Router's
// <Navigate replace/>; also carries a canonical on the target page so
// duplicate signals collapse. Kept as data so it's easy to audit + extend.
const LEGACY_301_MAP = [
  // Home aliases (typed URLs, old bookmarks, CMS defaults)
  ['/home', '/'],
  ['/index', '/'],
  ['/index.html', '/'],
  ['/agency', '/'],
  ['/digital-agency', '/'],
  ['/brightlabs', '/'],

  // Content hub
  ['/blog', '/insights'],
  ['/blog/*', '/insights'],
  ['/articles', '/insights'],
  ['/guides', '/insights'],
  ['/resources', '/insights'],

  // Work / case studies
  ['/portfolio', '/work'],
  ['/case-studies', '/work'],
  ['/case-study', '/work'],
  ['/projects', '/work'],

  // Sales / conversion
  ['/pricing', '/services'],
  ['/plans', '/services'],
  ['/get-started', '/start'],
  ['/enquire', '/start'],
  ['/enquiry', '/start'],
  ['/quote', '/start'],
  ['/book', '/start'],
  ['/book-a-call', '/start'],
  ['/consultation', '/start'],

  // Team / about
  ['/team', '/about'],
  ['/careers', '/about'],
  ['/about-us', '/about'],
  ['/who-we-are', '/about'],

  // Legal
  ['/cookies', '/privacy'],
  ['/cookie-policy', '/privacy'],
  ['/gdpr', '/privacy'],
  ['/tos', '/terms'],
  ['/terms-and-conditions', '/terms'],

  // Service aliases
  ['/services/paid', '/services/paid-growth'],
  ['/services/ads', '/services/paid-growth'],
  ['/services/google-ads', '/services/paid-growth'],
  ['/services/crm', '/services/custom-crm-development'],
  ['/services/web', '/services/websites'],
  ['/services/web-design', '/services/websites'],
  ['/services/social', '/services/creative-social'],

  // V1 lead-magnet + package pages that no longer exist
  ['/one-day-mvp', '/'],
  ['/for-teams', '/'],
  ['/free-module', '/insights'],
  ['/module-pack', '/insights'],
  ['/builds/content-social', '/services/creative-social'],
  ['/faq', '/#faq'],
  ['/packages/bright-start', '/small-business'],
  ['/packages/bright-local', '/small-business'],

  // Small business — canonicalise the sub-tree
  ['/small-business', '/'],
  ['/small-business/*', '/'],

  // India + legacy stubs (already present, moved here for consolidation)
  ['/legacy', '/'],
  ['/legacy/', '/'],
  ['/india', '/'],
  ['/india/', '/'],
];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <GAPageTracker />
          <Suspense fallback={null}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<V2Home />} />
            {INDUSTRIES.map((ind) => (
              <Route key={ind.slug} path={`/${ind.slug}`} element={<IndustryPage slug={ind.slug} />} />
            ))}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<V2Terms />} />
            <Route path="/privacy" element={<V2Privacy />} />
            <Route path="/norwich" element={<Norwich />} />
            <Route path="/norwich/digital-agency" element={<NorwichDigitalAgency />} />
            <Route path="/norwich/web-design" element={<NorwichWebDesign />} />
            <Route path="/norwich/seo" element={<NorwichSeo />} />
            <Route path="/norwich/google-ads" element={<NorwichGoogleAds />} />
            <Route path="/norwich/social-media" element={<NorwichSocialMedia />} />
            <Route path="/norwich/software-development" element={<NorwichSoftwareDevelopment />} />
            <Route path="/insights/website-redesign-vs-rebuild" element={<WebsiteRedesignVsRebuild />} />
            <Route path="/services/custom-crm-development" element={<CustomCrmDevelopment />} />
            <Route path="/insights/business-website-cost-uk" element={<BusinessWebsiteCostUk />} />
            <Route path="/insights/seo-vs-google-ads" element={<SeoVsGoogleAds />} />
            <Route path="/industries/saas-b2b-tech" element={<SaasB2bTech />} />
            <Route path="/london/digital-agency" element={<LondonDigitalAgency />} />
            <Route path="/b2b-digital-marketing-agency" element={<B2bDigitalMarketing />} />
            <Route path="/industries/recruitment-agencies" element={<RecruitmentAgencies />} />
            <Route path="/industries/private-clinics" element={<PrivateClinics />} />
            <Route path="/insights/website-not-generating-leads" element={<WebsiteNotGeneratingLeads />} />
            <Route path="/insights/google-ads-clicks-no-leads" element={<GoogleAdsClicksNoLeads />} />
            <Route path="/insights/when-do-you-need-custom-crm" element={<WhenNeedCustomCrm />} />
            <Route path="/insights/in-house-marketing-vs-agency" element={<InHouseVsAgency />} />
            <Route path="/insights/marketing-agency-vs-growth-partner" element={<AgencyVsGrowthPartner />} />
            <Route path="/onboarding/:token" element={<OnboardingPortal />} />
            <Route path="/client-review/:token" element={<ClientReview />} />
            <Route path="/proposal/:token" element={<PublicProposal />} />
            <Route path="/packages/:slug" element={<PackagePage />} />
            <Route path="/services/:slug" element={<V2ServiceDetail />} />
            <Route path="/builds/:slug" element={<BuildPage />} />
            <Route path="/about" element={<V2About />} />
            <Route path="/brand" element={<BrandPage />} />
            <Route path="/brand/" element={<BrandPage />} />
            <Route path="/norfolk" element={<LocalArea slug="norfolk" />} />
            <Route path="/east-anglia" element={<LocalArea slug="east-anglia" />} />
            {LOCAL_AREAS.filter((a) => !['norwich', 'norfolk', 'east-anglia'].includes(a.slug)).map((a) => (
              <Route key={a.slug} path={`/${a.slug}`} element={<LocalArea slug={a.slug} />} />
            ))}

            {/* Legacy redirects — 301 map for URLs that may sit in Google
                or in old inbound links. Keep DRY: add new entries to the
                LEGACY_301_MAP array so we don't grow a wall of <Route>s. */}
            {LEGACY_301_MAP.map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            <Route path="/register" element={<Navigate to="/contact" replace />} />
            <Route path="/portal" element={<Navigate to="/login" replace />} />
            <Route path="/portal/*" element={<Navigate to="/login" replace />} />
            <Route path="/payment/success" element={<Navigate to="/" replace />} />

            {/* BD Training (internal / shareable) */}
            <Route path="/training" element={<TrainingHome />} />
            <Route path="/training/day-1" element={<Day1 />} />
            <Route path="/training/day-2" element={<Day2 />} />
            <Route path="/training/day-3" element={<Day3 />} />
            <Route path="/training/day-4" element={<Day4 />} />
            <Route path="/training/roadmap" element={<Roadmap />} />
            <Route path="/training/notes-popout" element={<NotesPopout />} />
            <Route path="/cheat-sheet" element={<SalesCheatSheet />} />
            <Route path="/v2" element={<V2Index />} />
            <Route path="/v2/style" element={<V2StyleTile />} />
            <Route path="/v2/sitemap" element={<V2Sitemap />} />
            <Route path="/insights" element={<V2InsightsLibrary />} />
            <Route path="/sitemap" element={<V2Sitemap />} />
            <Route path="/v2/home" element={<V2Home />} />
            <Route path="/v2/work" element={<V2WorkIndex />} />
            <Route path="/v2/work/:slug" element={<V2WorkDetail />} />
            <Route path="/v2/small-business" element={<Navigate to="/" replace />} />
            <Route path="/v2/start" element={<V2StartAProject />} />
            <Route path="/start" element={<V2StartAProject />} />
            <Route path="/work" element={<V2WorkIndex />} />
            <Route path="/work/:slug" element={<V2WorkDetail />} />
            <Route path="/about" element={<V2About />} />
            <Route path="/services" element={<V2ServicesIndex />} />
            <Route path="/services/:slug" element={<V2ServiceDetail />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="lead-engine" element={<LeadEngine />} />
              <Route path="enquiries" element={<AdminEnquiries />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="services-handout" element={<AdminServicesHandout />} />
              <Route path="discovery-playbook" element={<DiscoveryPlaybook />} />
              <Route path="pricing-configurator" element={<PricingConfigurator />} />
              <Route path="delivery-playbooks" element={<AdminDeliveryPlaybooks />} />
              <Route path="sent-emails" element={<AdminSentEmails />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="conversations" element={<AdminConversations />} />
              <Route path="email-templates" element={<AdminEmailTemplates />} />
              <Route path="website-audit" element={<AdminWebsiteAudit />} />
              <Route path="seo-playbook" element={<AdminSeoPlaybook />} />
              <Route path="seo-pages" element={<AdminSeoPages />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="onboarding" element={<AdminOnboarding />} />
              <Route path="presentations" element={<AdminPresentations />} />
              <Route path="presentations/why-website" element={<WhyWebsiteDeck />} />
              <Route path="presentations/custom-crm" element={<CustomCrmDeck />} />
              <Route path="email-settings" element={<AdminEmailSettings />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
